import { type FC, type CSSProperties } from 'react'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart as RechartAreaChart,
} from 'recharts'
import cx from 'classnames'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'

export interface AreaChartProps extends DefaultProps {
  data: { date: string; value: number }[]
  onDataPointClick?: (_dataPoint: { date: string; value: number }) => void
  isPositive?: boolean
}

export const AreaChart: FC<AreaChartProps> = ({
  // Common default properties for each component
  className,
  id,
  testId,
  ariaLabel,
  data,
  onDataPointClick,
  isPositive = true,
}) => {
  if (data.length === 0) {
    return null
  }

  // Calculate min and max values for better Y-axis scaling
  const values = data.map((item) => item.value)
  const minValue = Math.min(...values)
  const maxValue = Math.max(...values)
  const padding = (maxValue - minValue) * 0.1 // 10% padding
  const yAxisDomain = [
    Math.max(0, minValue - padding), // Don't go below 0
    maxValue + padding,
  ]

  // Final class names
  const finalClasses = twMerge(
    'focus:outline-none [&>*]:focus:outline-none [&_svg]:focus:outline-none [&_svg]:!outline-none',
    className,
  )

  return (
    <div
      className={finalClasses}
      {...(testId ? { 'data-testid': testId } : {})}
      {...(id ? { id } : {})}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      aria-hidden="true"
      tabIndex={-1}
      style={
        {
          outline: 'none',
        } as CSSProperties
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <RechartAreaChart
          data={data}
          style={{ outline: 'none' }}
          onClick={(chartData) => {
            if (chartData.activeLabel && onDataPointClick) {
              const clickedPoint = data.find((point) => point.date === chartData.activeLabel)
              if (clickedPoint) {
                onDataPointClick(clickedPoint)
              }
            }
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#877F68', fontWeight: 600, fontFamily: 'Open Sans', color: '#877F68' }}
            tickLine={{ stroke: 'transparent' }}
            axisLine={{ stroke: '#e5e7eb', strokeDasharray: '3 3' }}
            tickFormatter={(value: string) => {
              const date = new Date(value)
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })
            }}
          />

          <YAxis domain={yAxisDomain} tickLine={false} axisLine={false} width={0} />

          <Tooltip
            cursor={{ stroke: isPositive ? '#008518' : '#DA3939', strokeWidth: 1 }}
            contentStyle={{
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '8px 12px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            labelStyle={{ display: 'none' }}
            formatter={(value, _, item) => [
              <div
                key="tooltip"
                onClick={() => {
                  if (onDataPointClick) {
                    onDataPointClick(item.payload as { date: string; value: number; change: number })
                  }
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && onDataPointClick) {
                    onDataPointClick(item.payload as { date: string; value: number; change: number })
                  }
                }}
                tabIndex={0}
                role="button"
                style={{ cursor: 'pointer' }}
              >
                <div
                  className={cx('typo-primary-base-semibold mb-3xs', isPositive ? 'text-[#008518]' : 'text-[#DA3939]')}
                >
                  ${value}
                </div>
                <div className="typo-primary-sm text-text-meta">
                  {new Date((item.payload as { date: string }).date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
              </div>,
            ]}
          />
          <Area
            type="linear"
            dataKey="value"
            stroke="#877F68"
            strokeWidth={1}
            fillOpacity={1}
            fill="transparent"
            activeDot={{ fill: isPositive ? '#008518' : '#DA3939', strokeWidth: 2, stroke: '#ffffff', r: 6 }}
            animationDuration={0}
            isAnimationActive={false}
          />
        </RechartAreaChart>
      </ResponsiveContainer>
    </div>
  )
}
